import { ReactElement } from 'react'
import { observer } from 'mobx-react-lite'
import { ProductLocationModel, ProductModel } from 'interfaces'
import BaseDrawer from 'src/ui-components/modals/BaseDrawer'
import { Thumbnail } from '../../../../../src/ui-components/products/Thumbnail'
import styled from 'styled-components'
import { I18n, useBrandImage } from 'common'
import { Divider } from '@mui/material'
import OtherPrices from './OtherPrices'

export interface Price {
  priceCodeName: string
  priceValue: number
}

interface PriceInformation {
  openPriceDrawer: boolean
  Image: string
  description: string
  productDetails: ProductModel
  activeLocation: ProductLocationModel
  corePrice: number
  listPrice: number
  YourCost: number
  coreCost: number
  otherPrices: Array<Price>
  selectedQuantity: number
  minWidth: string
  closeDrawer: (show: boolean) => void
}

export const ProdcutPriceDetialsDrawer = observer(
  ({
    openPriceDrawer,
    Image,
    description,
    productDetails,
    corePrice,
    listPrice,
    YourCost,
    coreCost,
    otherPrices,
    selectedQuantity,
    minWidth,
    closeDrawer,
  }: PriceInformation): ReactElement => {
    const [brandImgUrl] = useBrandImage(productDetails)

    const handledrawer = (show: boolean) => {
      closeDrawer(show)
    }
    const DrawerBody = (
      <MobileDrawerBody className="align-items-center justify-content-center">
        <HeaderContent>
          <DrawerImages>
            <Thumbnail
              urlOrBase64={Image}
              width={100}
              height={100}
              placeholder
            />

            {brandImgUrl && (
              <BrandWrapper>
                <Thumbnail
                  urlOrBase64={brandImgUrl}
                  width={100}
                  height={100}
                  alt="brand"
                />
              </BrandWrapper>
            )}
          </DrawerImages>
          <ProductInformation>
            <ProductName>{description}</ProductName>
            <MobilePriceInformation>
              <NoWrap>
                <span style={{ whiteSpace: 'nowrap' }}>
                  <I18n name="partNum" />
                  :&nbsp;
                </span>
                <b>{productDetails.partNumber}</b>
              </NoWrap>
              <NoWrap>
                <I18n name="lineCode" />: <b>{productDetails.lineCode}</b>
              </NoWrap>
            </MobilePriceInformation>
          </ProductInformation>
        </HeaderContent>
        <Divider style={{ margin: '20px' }} />
        <ProductInformationWrapper>
          <ProductInformation>
            <ListHeader>
              <I18n name="listPriceBreakdown" />
            </ListHeader>
            <ListPriceCost>
              <ListPriceWrap>
                <I18n name="listPrice" />
              </ListPriceWrap>
              <ListPriceValue>{'$' + `${listPrice}`}</ListPriceValue>
            </ListPriceCost>
            <ListPriceCost>
              <ListPriceWrap>
                <I18n name="corePrice" />
              </ListPriceWrap>
              <ListPriceValue>{'$' + `${corePrice}`}</ListPriceValue>
            </ListPriceCost>
          </ProductInformation>
          <ProductInformation>
            <ListHeader>
              <I18n name="costBreakdown" />
            </ListHeader>
            <ListPriceCost>
              <ListPriceWrap>
                <I18n name="yourCost" />
              </ListPriceWrap>
              <ListPriceValue>{'$' + `${YourCost}`}</ListPriceValue>
            </ListPriceCost>
            <ListPriceCost>
              <ListPriceWrap>
                <I18n name="coreCost" />
              </ListPriceWrap>
              <ListPriceValue>{'$' + `${coreCost}`}</ListPriceValue>
            </ListPriceCost>
            <ListPriceCost>
              <ListPriceWrap>
                <I18n name="environmentCost" />
              </ListPriceWrap>
              <ListPriceValue></ListPriceValue>
            </ListPriceCost>
          </ProductInformation>
        </ProductInformationWrapper>
        <OtherPricesWrapper>
          {otherPrices
            ?.slice(0, 2)
            .filter((item) => item.priceValue > 0)
            .map((item, index) => (
              <OtherPrices item={item} key={index} />
            ))}
        </OtherPricesWrapper>
        <Divider style={{ margin: '20px' }} />
        <TotalCostWrapper>
          <ListPriceCost style={{ fontWeight: 'bold', marginTop: '30px' }}>
            <ListPriceWrap>
              <I18n name="TotalCostInHours" />
            </ListPriceWrap>
            <ListPriceValue>
              {'$' + `${selectedQuantity * YourCost}`}
            </ListPriceValue>
          </ListPriceCost>
        </TotalCostWrapper>
      </MobileDrawerBody>
    )

    return (
      <BaseDrawer
        open={openPriceDrawer}
        title="costDetails"
        body={DrawerBody}
        onClose={() => handledrawer(false)}
        width="100%"
        height="100%"
        minWidth={minWidth}
      />
    )
  }
)

const MobileDrawerBody = styled.div``

const TotalCostWrapper = styled.div`
  margin: 0px 10px 0px 10px;
`

const OtherPricesWrapper = styled.div`
  margin: 0px 10px 0px 10px;
`

const ProductInformation = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
`

const ProductInformationWrapper = styled.div`
  margin: 25px 10px 10px 10px;
  display: flex;
  flex-direction: column;
  row-gap: 25px;
`

const DrawerImages = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 30px;
  width: 30%;
`
const ListHeader = styled.div`
  font-size: 20px;
  font-weight: bold;
  line-height: 24px;
`
const ListPriceCost = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
`
const ListPriceWrap = styled.div``
const ListPriceValue = styled.div``
const HeaderContent = styled.div`
  display: flex;
  flex-direction: row;
`

const BrandWrapper = styled.div`
  margin-top: 20px;
  cursor: pointer;
`
const ProductName = styled.div`
  font-family: Oswald, serif;
  font-size: 20px;
  font-weight: bold;
  line-height: 30px;
  max-width: 250px;
`
const MobilePriceInformation = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`
const NoWrap = styled.span`
  white-space: nowrap;
`
